import { UniqueIdentifier } from "@dnd-kit/core";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import {
  NEEDS_ATTENTION_FOLDER_NAME,
  ReturnSystemView,
} from "@redotech/redo-model/views/default-return-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import {
  TreeItem,
  TreeItemType,
  TreeViewItem,
} from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  OrderManagementSuiteStatusesContext,
  RETURNS_ICON,
  RETURNS_ICON_FILLED,
} from "../../../../redo-suite/order-management-cloud";
import { ReturnViewOrderingContext } from "../../../../services/returns/returns-view-ordering-service";
import { useViewFolderLogic } from "./use-view-folder-logic";
import {
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

export const ReturnsItem = memo(function ReturnsItem() {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { returnsEnabled } = useContext(OrderManagementSuiteStatusesContext);
  const urlPrefix = routes.returnsHref;
  const returnsInstantExchangeEnabled = team.settings.exchanges.allowInstant;

  const hasPermissionToViewItem = useHasPermission(
    OrderManagementPermissions.VIEW_RETURN,
  );

  const returnViewOrderingContext = useRequiredContext(
    ReturnViewOrderingContext,
  );
  const { notificationCounts } = returnViewOrderingContext;

  const allViews = useMemo(() => {
    return [...returnViewOrderingContext.views].filter(
      (view) => view.name !== ReturnSystemView.CREATE,
    );
  }, [returnViewOrderingContext]);

  const allFolders = useMemo(() => {
    return [...returnViewOrderingContext.folders];
  }, [returnViewOrderingContext]);

  const {
    viewsWithoutFolders,
    foldersWithViews,
    folderNotificationCounts,
    totalNotificationCount,
  } = useViewFolderLogic<"returns">({
    allViews,
    allFolders,
    notificationCounts,
  });

  const giftReceiptsEnabled = !!team.settings.returns?.enableGiftReceipts;

  const { sortableItems, needsAttentionFolderExists } = useMemo(() => {
    const items = viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: notificationCounts,
      folderCounts: folderNotificationCounts,
      hideZeroNotifications: false,
    });

    const needsAttentionFolder = items.find(
      (item) =>
        item.type === TreeItemType.Folder &&
        item.name === NEEDS_ATTENTION_FOLDER_NAME,
    );

    if (
      !needsAttentionFolder ||
      needsAttentionFolder.type !== TreeItemType.Folder
    ) {
      return { sortableItems: items, needsAttentionFolderExists: false };
    }

    const extraChildren: TreeViewItem[] = [];

    if (returnsInstantExchangeEnabled) {
      extraChildren.push({
        id: "returns-instant-exchange",
        name: "Instant exchange",
        friendlyName: "Instant exchange",
        type: TreeItemType.View,
        actionHref: routes.getReturnsSubRoute("instant-exchange"),
      });
    }

    if (giftReceiptsEnabled) {
      extraChildren.push({
        id: "gift-receipt-requests",
        name: "Gift receipt requests",
        friendlyName: "Gift receipt requests",
        type: TreeItemType.View,
        actionHref: routes.getReturnsSubRoute("gift-receipt-requests"),
      });
    }

    needsAttentionFolder.children = [
      ...needsAttentionFolder.children,
      ...extraChildren,
    ];

    return { sortableItems: items, needsAttentionFolderExists: true };
  }, [
    viewsWithoutFolders,
    foldersWithViews,
    urlPrefix,
    notificationCounts,
    folderNotificationCounts,
    returnsInstantExchangeEnabled,
    giftReceiptsEnabled,
    routes,
  ]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    returnViewOrderingContext.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const { views, folders } = treeToViewsAndFolders<"returns">(
      flattenedItems,
      allViews,
      allFolders,
    );
    returnViewOrderingContext.updateOrdering(views, folders);
  });

  const inmarIntegrationLoad = useLoad(async () => {
    return await rpcClient.getInmarIntegration({});
  }, []);

  const anyChildSelected = useMatch(`${urlPrefix}/*`);

  if (!returnsEnabled || !hasPermissionToViewItem) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: urlPrefix }}
      anyChildSelected={!!anyChildSelected}
      FilledIcon={RETURNS_ICON_FILLED}
      friendlyName="Returns"
      id="returns"
      notificationCount={totalNotificationCount}
      UnfilledIcon={RETURNS_ICON}
    >
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getReturnsSubRoute("instant-exchange"),
        }}
        friendlyName="Instant exchange"
        hidden={!returnsInstantExchangeEnabled || needsAttentionFolderExists}
        id="returns-instant-exchange"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getReturnsSubRoute("gift-receipt-requests"),
        }}
        friendlyName="Gift receipt requests"
        hidden={!giftReceiptsEnabled || needsAttentionFolderExists}
        id="gift-receipt-requests"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getReturnsSubRoute("inmar-blind-returns"),
        }}
        friendlyName="Inmar blind returns"
        hidden={!inmarIntegrationLoad.value?.inmarIntegration?.enabled}
        id="inmar-blind-returns"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getReturnsSubRoute("all-returns"),
        }}
        friendlyName="All returns"
        id="all-returns"
      />
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.getReturnsSubRoute("create") }}
        friendlyName="+ Add new view"
        id="orders-returns-create"
      />
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            returnViewOrderingContext.openCreateFolderModal();
          },
        }}
        friendlyName="+ Add new folder"
        id="orders-returns-create-folder"
      />
    </RedoSecondaryNavItem>
  );
});
